import { supabase } from './supabase';
import { getUserBusinessId, getNextDocumentNumber } from './supabaseDb';

type DbResult<T> = { data: T | null; error: string | null };

function handleError(err: any): string {
  if (!err) return 'Bilinmeyen hata';
  return err.message || err.details || String(err);
}

function sanitizeItem<T extends Record<string, any>>(item: T): T {
  const cleaned: any = {};
  for (const [key, value] of Object.entries(item)) {
    if (value === undefined) continue;
    cleaned[key] = value;
  }
  return cleaned as T;
}

// ============================================================
// QUOTES CRUD
// ============================================================

export async function getQuotes(): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('quotes')
      .select('*')
      .eq('business_id', bizId)
      .order('created_at', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as any[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getQuoteById(id: string): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('quotes')
      .select('*')
      .eq('id', id)
      .eq('business_id', bizId)
      .maybeSingle();

    if (error) return { data: null, error: handleError(error) };
    return { data: data as any, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function createQuote(data: any): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data: created, error } = await supabase
      .from('quotes')
      .insert({ ...sanitizeItem(data), business_id: bizId })
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data: created, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function updateQuote(id: string, data: any): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data: updated, error } = await supabase
      .from('quotes')
      .update({ ...sanitizeItem(data), updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('business_id', bizId)
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data: updated, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function updateQuoteStatus(id: string, status: string, note?: string): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const updates: any = { status, updated_at: new Date().toISOString() };
    if (status === 'Onaylandı' || status === 'Satışa Dönüştürüldü') {
      updates.approved_at = new Date().toISOString();
    }

    const { data: updated, error } = await supabase
      .from('quotes')
      .update(updates)
      .eq('id', id)
      .eq('business_id', bizId)
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };

    // Log activity
    await supabase.from('quote_activities').insert({
      business_id: bizId,
      quote_id: id,
      activity_type: 'status_change',
      new_status: status,
      note: note || '',
      created_by: 'Admin',
    });

    return { data: updated, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function deleteQuote(id: string): Promise<DbResult<boolean>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    // Delete related records first
    await supabase.from('quote_payment_plan').delete().eq('quote_id', id).eq('business_id', bizId);
    await supabase.from('quote_note_terms').delete().eq('quote_id', id).eq('business_id', bizId);
    await supabase.from('quote_items').delete().eq('quote_id', id).eq('business_id', bizId);
    await supabase.from('quote_revisions').delete().eq('quote_id', id).eq('business_id', bizId);
    await supabase.from('quote_activities').delete().eq('quote_id', id).eq('business_id', bizId);

    const { error } = await supabase.from('quotes').delete().eq('id', id).eq('business_id', bizId);
    if (error) return { data: null, error: handleError(error) };
    return { data: true, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// QUOTE ITEMS
// ============================================================

export async function getQuoteItems(quoteId: string): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('quote_items')
      .select('*')
      .eq('business_id', bizId)
      .eq('quote_id', quoteId)
      .order('sort_order', { ascending: true });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as any[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function createQuoteItem(data: any): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data: created, error } = await supabase
      .from('quote_items')
      .insert({ ...sanitizeItem(data), business_id: bizId })
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data: created, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function createQuoteItems(items: any[]): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const withBiz = items.map(item => ({ ...sanitizeItem(item), business_id: bizId }));
    const { data, error } = await supabase.from('quote_items').insert(withBiz).select();
    if (error) return { data: null, error: handleError(error) };
    return { data: data as any[], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function updateQuoteItem(id: string, data: any): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data: updated, error } = await supabase
      .from('quote_items')
      .update(sanitizeItem(data))
      .eq('id', id)
      .eq('business_id', bizId)
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data: updated, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function deleteQuoteItems(quoteId: string): Promise<DbResult<boolean>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { error } = await supabase
      .from('quote_items')
      .delete()
      .eq('quote_id', quoteId)
      .eq('business_id', bizId);

    if (error) return { data: null, error: handleError(error) };
    return { data: true, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// QUOTE PAYMENT PLAN
// ============================================================

export async function getQuotePaymentPlan(quoteId: string): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('quote_payment_plan')
      .select('*')
      .eq('business_id', bizId)
      .eq('quote_id', quoteId)
      .order('installment_no', { ascending: true });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as any[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function createQuotePaymentPlans(items: any[]): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const withBiz = items.map(item => ({ ...sanitizeItem(item), business_id: bizId }));
    const { data, error } = await supabase.from('quote_payment_plan').insert(withBiz).select();
    if (error) return { data: null, error: handleError(error) };
    return { data: data as any[], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function deleteQuotePaymentPlan(quoteId: string): Promise<DbResult<boolean>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { error } = await supabase
      .from('quote_payment_plan')
      .delete()
      .eq('quote_id', quoteId)
      .eq('business_id', bizId);

    if (error) return { data: null, error: handleError(error) };
    return { data: true, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// QUOTE REVISIONS
// ============================================================

export async function getQuoteRevisions(quoteId: string): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('quote_revisions')
      .select('*')
      .eq('business_id', bizId)
      .eq('quote_id', quoteId)
      .order('revision_no', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as any[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function createQuoteRevision(data: any): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data: created, error } = await supabase
      .from('quote_revisions')
      .insert({ ...sanitizeItem(data), business_id: bizId })
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data: created, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// QUOTE ACTIVITIES
// ============================================================

export async function getQuoteActivities(quoteId: string): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('quote_activities')
      .select('*')
      .eq('business_id', bizId)
      .eq('quote_id', quoteId)
      .order('created_at', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as any[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function createQuoteActivity(data: any): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data: created, error } = await supabase
      .from('quote_activities')
      .insert({ ...sanitizeItem(data), business_id: bizId })
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data: created, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// QUOTE NOTE TERMS (Senet bilgileri)
// ============================================================

export async function getQuoteNoteTerms(quoteId: string): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('quote_note_terms')
      .select('*')
      .eq('business_id', bizId)
      .eq('quote_id', quoteId)
      .maybeSingle();

    if (error) return { data: null, error: handleError(error) };
    return { data: data as any, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function createQuoteNoteTerm(data: any): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data: created, error } = await supabase
      .from('quote_note_terms')
      .insert({ ...sanitizeItem(data), business_id: bizId })
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data: created, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function upsertQuoteNoteTerm(quoteId: string, data: any): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    // Delete existing then insert
    await supabase.from('quote_note_terms').delete().eq('quote_id', quoteId).eq('business_id', bizId);

    const { data: created, error } = await supabase
      .from('quote_note_terms')
      .insert({ ...sanitizeItem(data), business_id: bizId, quote_id: quoteId })
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data: created, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function deleteQuoteNoteTerms(quoteId: string): Promise<DbResult<boolean>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { error } = await supabase
      .from('quote_note_terms')
      .delete()
      .eq('quote_id', quoteId)
      .eq('business_id', bizId);

    if (error) return { data: null, error: handleError(error) };
    return { data: true, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// GENERATE QUOTE NUMBER
// ============================================================

export async function generateQuoteNo(): Promise<DbResult<string>> {
  return getNextDocumentNumber('TKL');
}

// ============================================================
// APPROVE & CONVERT QUOTE
// ============================================================

export async function approveAndConvertQuote(quoteId: string, payload: any): Promise<DbResult<any>> {
  try {
    const { data, error } = await supabase.rpc('approve_and_convert_quote', {
      p_quote_id: quoteId,
      p_approval_payload: payload,
    });

    if (error) return { data: null, error: handleError(error) };
    return { data, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// DUPLICATE QUOTE
// ============================================================

export async function duplicateQuote(quoteId: string): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    // Get original quote
    const { data: original, error } = await supabase
      .from('quotes')
      .select('*')
      .eq('id', quoteId)
      .eq('business_id', bizId)
      .maybeSingle();

    if (error || !original) return { data: null, error: error ? handleError(error) : 'Teklif bulunamadı' };

    const orig = original as any;

    // Generate new quote number
    const { data: newNo } = await generateQuoteNo();

    // Create new quote (without id, created_at, etc)
    const { data: created } = await supabase
      .from('quotes')
      .insert({
        business_id: bizId,
        quote_no: newNo,
        revision_no: 1,
        status: 'Taslak',
        prospect_name: orig.prospect_name,
        phone: orig.phone,
        secondary_phone: orig.secondary_phone,
        identity_no: orig.identity_no,
        city: orig.city,
        district: orig.district,
        address: orig.address,
        customer_note: orig.customer_note,
        quote_date: new Date().toISOString().split('T')[0],
        valid_until: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
        subtotal: orig.subtotal,
        discount_amount: orig.discount_amount,
        shipping_amount: orig.shipping_amount,
        assembly_amount: orig.assembly_amount,
        tax_amount: orig.tax_amount,
        total_amount: orig.total_amount,
        total_cost: orig.total_cost,
        estimated_profit: orig.estimated_profit,
        payment_type: orig.payment_type,
        planned_deposit: orig.planned_deposit,
        planned_remaining: orig.planned_remaining,
        installment_count: orig.installment_count,
        first_payment_date: orig.first_payment_date,
        delivery_date: orig.delivery_date,
        delivery_time: orig.delivery_time,
        delivery_address: orig.delivery_address,
        delivery_note: orig.delivery_note,
        public_note: orig.public_note,
        internal_note: orig.internal_note,
        prepared_by: orig.prepared_by,
      })
      .select()
      .single();

    if (!created) return { data: null, error: 'Kopya oluşturulamadı' };

    const newQuote = created as any;

    // Copy items
    const { data: items } = await supabase
      .from('quote_items')
      .select('*')
      .eq('quote_id', quoteId)
      .eq('business_id', bizId)
      .order('sort_order', { ascending: true });

    if (items && items.length > 0) {
      const newItems = items.map((item: any) => ({
        business_id: bizId,
        quote_id: newQuote.id,
        category: item.category,
        product_id: item.product_id,
        product_type: item.product_type,
        brand: item.brand,
        product_name: item.product_name,
        model_code: item.model_code,
        serial_no: item.serial_no,
        color: item.color,
        dimensions: item.dimensions,
        quantity: item.quantity,
        unit_cost: item.unit_cost,
        unit_price: item.unit_price,
        discount_amount: item.discount_amount,
        line_total: item.line_total,
        description: item.description,
        sort_order: item.sort_order,
      }));
      await supabase.from('quote_items').insert(newItems);
    }

    // Copy payment plan
    const { data: plans } = await supabase
      .from('quote_payment_plan')
      .select('*')
      .eq('quote_id', quoteId)
      .eq('business_id', bizId)
      .order('installment_no', { ascending: true });

    if (plans && plans.length > 0) {
      const newPlans = plans.map((plan: any) => ({
        business_id: bizId,
        quote_id: newQuote.id,
        installment_no: plan.installment_no,
        due_date: plan.due_date,
        amount: plan.amount,
        payment_method: plan.payment_method,
        description: plan.description,
      }));
      await supabase.from('quote_payment_plan').insert(newPlans);
    }

    return { data: newQuote, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// SAVE FULL QUOTE (items + payment plan)
// ============================================================

export async function saveFullQuote(
  quoteData: any,
  items: any[],
  paymentPlan: any[],
  noteTermsData: any | null = null,
  existingQuoteId?: string | null
): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    let quoteId: string;

    if (existingQuoteId) {
      // Update existing quote
      await supabase
        .from('quotes')
        .update({ ...sanitizeItem(quoteData), updated_at: new Date().toISOString() })
        .eq('id', existingQuoteId)
        .eq('business_id', bizId);

      quoteId = existingQuoteId;

      // Delete old items, payment plan and note terms
      await supabase.from('quote_items').delete().eq('quote_id', quoteId).eq('business_id', bizId);
      await supabase.from('quote_payment_plan').delete().eq('quote_id', quoteId).eq('business_id', bizId);
      await supabase.from('quote_note_terms').delete().eq('quote_id', quoteId).eq('business_id', bizId);
    } else {
      // Generate quote number
      const { data: quoteNo } = await generateQuoteNo();
      const qData = { ...quoteData, quote_no: quoteNo };

      const { data: created } = await supabase
        .from('quotes')
        .insert({ ...sanitizeItem(qData), business_id: bizId })
        .select()
        .single();

      if (!created) return { data: null, error: 'Teklif oluşturulamadı' };
      quoteId = (created as any).id;
    }

    // Insert items
    if (items.length > 0) {
      const withBiz = items.map((item, idx) => ({
        ...sanitizeItem(item),
        business_id: bizId,
        quote_id: quoteId,
        sort_order: idx,
      }));
      await supabase.from('quote_items').insert(withBiz);
    }

    // Insert payment plan
    if (paymentPlan.length > 0) {
      const withBiz = paymentPlan.map(plan => ({
        ...sanitizeItem(plan),
        business_id: bizId,
        quote_id: quoteId,
      }));
      await supabase.from('quote_payment_plan').insert(withBiz);
    }

    // Insert note terms if provided (Senet)
    if (noteTermsData && (noteTermsData.book_no || noteTermsData.page_no || noteTermsData.due_date)) {
      await supabase.from('quote_note_terms').upsert({
        ...sanitizeItem(noteTermsData),
        business_id: bizId,
        quote_id: quoteId,
      }, { onConflict: 'quote_id,business_id' });
    }

    // Return full quote
    const { data: fullQuote } = await supabase
      .from('quotes')
      .select('*')
      .eq('id', quoteId)
      .maybeSingle();

    return { data: fullQuote, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}